import { useEffect, useRef, useState } from 'react';
import { uiText } from '../../i18n';

/** Запись запускается только нажатием. При закрытии мастера микрофон освобождается. */
export function useRecorder(onSample: (file: File, durationMs: number) => void, maxDurationMs: number) {
  const [state, setState] = useState<'idle' | 'requesting' | 'recording'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const session = useRef<{ cancel(): void; stop(): void } | null>(null);
  const mounted = useRef(true);
  const callback = useRef(onSample);
  useEffect(() => { callback.current = onSample; }, [onSample]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; session.current?.cancel(); session.current = null; };
  }, []);

  async function start() {
    // React state updates are asynchronous; the session fences two clicks in the same turn.
    if (session.current || !mounted.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError(uiText('Запись здесь недоступна. Откройте приложение по HTTPS или загрузите аудиофайл.'));
      return;
    }
    if (!Number.isFinite(maxDurationMs) || maxDurationMs <= 0) return;
    let cancelled = false, failed = false, media: MediaStream | null = null;
    let instance: MediaRecorder | null = null, timer = 0, began = 0, duration = 0;
    let stopping = false;
    const chunks: Blob[] = [];
    const ended: Array<[MediaStreamTrack, () => void]> = [];
    const active = () => mounted.current && !cancelled && session.current === current;
    const release = () => {
      window.clearInterval(timer);
      for (const [track, handler] of ended) track.removeEventListener?.('ended', handler);
      ended.length = 0;
      media?.getTracks().forEach(track => track.stop()); media = null;
    };
    const cancel = () => {
      cancelled = true;
      if (instance) {
        instance.onstop = null; instance.onerror = null; instance.ondataavailable = null;
        if (instance.state !== 'inactive') { try { instance.stop(); } catch { /* already stopped */ } }
      }
      release();
    };
    const fail = () => {
      if (!active() || failed) return;
      failed = true; cancel(); session.current = null;
      setState('idle'); setError(uiText('Запись прервалась. Попробуйте ещё раз или загрузите файл.'));
    };
    const stop = () => {
      if (!instance) { cancel(); if (mounted.current) setState('idle'); session.current = null; return; }
      if (stopping || instance.state === 'inactive') return;
      stopping = true; duration = Math.min(maxDurationMs, performance.now() - began);
      window.clearInterval(timer);
      try { instance.stop(); } catch { fail(); }
    };
    const current = { cancel, stop }; session.current = current;
    setError(''); setState('requesting');
    try {
      const granted = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!active()) { granted.getTracks().forEach(track => track.stop()); return; }
      media = granted;
      const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
        .find(type => MediaRecorder.isTypeSupported(type));
      instance = new MediaRecorder(media, mime ? { mimeType: mime } : undefined);
      const recording = instance;
      recording.ondataavailable = event => { if (active() && !failed && event.data.size) chunks.push(event.data); };
      recording.onstop = () => {
        release();
        if (!active() || failed) return;
        if (!stopping) { fail(); return; }
        session.current = null; setState('idle');
        const type = recording.mimeType.split(';')[0];
        const extension = type === 'audio/mp4' ? 'm4a' : type === 'audio/ogg' ? 'ogg' : 'webm';
        const file = new File(chunks, `Запись-${Date.now()}.${extension}`, { type });
        if (file.size && duration > 0) callback.current(file, Math.round(duration));
        else setError(uiText('Не удалось прочитать аудио. Попробуйте ещё раз или загрузите файл.'));
      };
      recording.onerror = fail;
      for (const track of media.getTracks()) { track.addEventListener?.('ended', fail); ended.push([track, fail]); }
      recording.start(); began = performance.now(); setElapsed(0); setState('recording');
      timer = window.setInterval(() => {
        if (!active() || stopping) return;
        const duration = Math.min(maxDurationMs, performance.now() - began); setElapsed(duration);
        if (duration >= maxDurationMs) stop();
      }, 200);
    } catch {
      if (!active()) return;
      cancel(); session.current = null;
      setState('idle'); setError(uiText('Не удалось включить микрофон. Разрешите доступ или загрузите файл.'));
    }
  }
  return { state, elapsed, error, start, stop: () => session.current?.stop(), cancel: () => {
    session.current?.cancel(); session.current = null; if (mounted.current) setState('idle');
  } };
}
